import { createTheme, ThemeOptions } from '@mui/material/styles';

// Palette colori EffAi
const effaiColors = {
  primary: {
    main: '#00A19C',
    dark: '#008C87',
    light: '#92D6D5',
  },
  secondary: {
    main: '#80142B',
  },
  dark: '#181818',
  light: '#E6E6E6',
  background: {
    dark: {
      level1: '#1A1A1A',
      level2: '#3F3F3F',
    },
    light: {
      level1: '#F2F2F2',
      level2: '#E6E6E6',
    },
  },
  // Colori condizionali per tabelle (usati per indicatori di stato)
  conditional: {
    error: '#EF5350', // Rosso
    warning: '#FF9800', // Arancione
    success: '#4CAF50', // Verde
  },
};

// Estendi il tipo Theme per includere i colori condizionali
declare module '@mui/material/styles' {
  interface Palette {
    conditional: {
      error: string;
      warning: string;
      success: string;
    };
  }
  interface PaletteOptions {
    conditional?: {
      error?: string;
      warning?: string;
      success?: string;
    };
  }
}

export const effaiTheme = createTheme({
  palette: {
    mode: 'dark', // Default dark mode
    primary: {
      main: effaiColors.primary.main,
      dark: effaiColors.primary.dark,
      light: effaiColors.primary.light,
      contrastText: '#fff',
    },
    secondary: {
      main: effaiColors.secondary.main,
      contrastText: '#fff',
    },
    background: {
      default: effaiColors.background.dark.level1,
      paper: effaiColors.background.dark.level2,
    },
    text: {
      primary: effaiColors.light,
      secondary: effaiColors.primary.light,
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    conditional: {
      error: effaiColors.conditional.error,
      warning: effaiColors.conditional.warning,
      success: effaiColors.conditional.success,
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          // Webkit browsers (Chrome, Safari, Edge)
          '&::-webkit-scrollbar': {
            width: '20px',
            height: '20px',
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
          },
          '&::-webkit-scrollbar-track': {
            background: effaiColors.background.dark.level2,
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#6B6B6B',
            borderRadius: '999px',
            border: `3px solid ${effaiColors.background.dark.level2}`,
            '&:hover': {
              background: '#8B8B8B',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'auto',
          scrollbarColor: `#6B6B6B ${effaiColors.background.dark.level2}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: effaiColors.background.dark.level1,
          color: effaiColors.light,
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
          color: effaiColors.light,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.dark.level2,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#3F3F3F',
          color: '#fff',
          borderRadius: '4px',
          marginBottom: '8px',
          border: '1px solid gray',
          '&:before': {
            display: 'none', // rimuove la linea grigia di default
          },
          '&.Mui-expanded': {
            backgroundColor: '#3F3F3F',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: '#fff',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid gray', // bordo esterno
          borderRadius: 8,
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid gray', // linea orizzontale tra le righe
          },
          '& .MuiDataGrid-columnHeader': {
            //height: '36px',
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'gray', // colore dei separatori verticali tra colonne
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid gray', // linea sotto l’intestazione
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid gray', // linea sopra il footer
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 4,
            },
          },
        },
      },
    },
  },
} as ThemeOptions);

// Tema chiaro (opzionale, per uso futuro)
export const effaiLightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: effaiColors.primary.main,
      dark: effaiColors.primary.dark,
      light: effaiColors.primary.light,
      contrastText: '#fff',
    },
    secondary: {
      main: effaiColors.secondary.main,
      contrastText: '#fff',
    },
    background: {
      default: effaiColors.background.light.level1,
      paper: effaiColors.background.light.level2,
    },
    text: {
      primary: effaiColors.dark,
      secondary: effaiColors.primary.dark,
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          // Webkit browsers (Chrome, Safari, Edge)
          '&::-webkit-scrollbar': {
            width: '20px',
            height: '20px',
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
          },
          '&::-webkit-scrollbar-track': {
            background: effaiColors.background.light.level2,
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#9E9E9E',
            borderRadius: '999px',
            border: `3px solid ${effaiColors.background.light.level2}`,
            '&:hover': {
              background: '#BDBDBD',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'auto',
          scrollbarColor: `#9E9E9E ${effaiColors.background.light.level2}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: effaiColors.background.light.level1,
          color: effaiColors.dark,
          borderTopRightRadius: 20,
          borderBottomRightRadius: 20,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.light.level2,
          color: effaiColors.dark,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.light.level2,
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: effaiColors.background.light.level2,
        },
      },
    },
  },
} as ThemeOptions);
